/**
 * GnssFeed, runs the simulated GNSS source while the simulator is on.
 *
 * This is the simulator side of the seam. When the sim is running it builds a
 * SimulatedGnssSource seeded from the boat's start position and the scenario
 * clock, registers it as the active source, and lets it tick. The source reads
 * live helm and environment state each tick (heading, STW, tide, wind-derived
 * leeway, rate) and emits NMEA. It advances the scenario clock so tide displays
 * keep up.
 *
 * In the live app the sim is on by default (sim store `running`), so the boat
 * acquires a fix and appears without anyone placing it. If no boat has been
 * placed, we seed a sensible demo start (DEFAULT_SIM_START) so there's a vessel
 * to feed; if one has, we leave it alone. Either way it writes nothing to own
 * ship after that, that's GnssIngest's job, off the NMEA feed.
 */
import { useEffect } from 'react';
import { useChartPlotterStore } from '@/store';
import { useEnvStore } from '@/store/environment';
import { useSimStore } from '@/store/sim';
import { usePositionSourceStore } from '@/store/positionSource';
import { useGnssStore } from '@/store/gnss';
import {
  SimulatedGnssSource,
  DEFAULT_SIM_START,
  shouldReseatSimBoat,
  type SimInputs,
} from '@/gnss';
import { leewayFromWind } from '@/ownship/sim';

export function GnssFeed() {
  const running = useSimStore((s) => s.running);
  const sourceKind = usePositionSourceStore((s) => s.sourceKind);

  useEffect(() => {
    // Only the simulator side: the device GPS is handled by DeviceGnssFeed.
    if (sourceKind !== 'sim' || !running) return;

    const store = useChartPlotterStore.getState();
    const existing = store.ownship.position;

    // Seed the training boat on the practice area when there's no boat yet, OR
    // when the position on screen is a leftover REAL device fix (we've just
    // switched from Device GNSS to Simulated). Otherwise the sim would spawn on
    // the real position with no helm command and sit there, looking like the
    // switch did nothing (fb-ms1is17a-5). A gentle heading and speed keep it on
    // screen and let it drive over the practice area straight away.
    const reseat = shouldReseatSimBoat(existing, useGnssStore.getState().positionOrigin);
    // existing is non-null whenever reseat is false (a null position always reseats).
    const startPosition: [number, number] = reseat ? DEFAULT_SIM_START.position : existing!;
    if (reseat) {
      store.setOwnship({
        position: startPosition,
        heading: DEFAULT_SIM_START.headingDeg,
        stw: DEFAULT_SIM_START.stwKn,
      });
      // Mark the reseat as sim-origin now, so a StrictMode double-run (or the
      // health readout) sees the training boat, not the old real fix.
      useGnssStore.getState().setPositionOrigin('sim');
      // Don't draw a track line from the old real position across to here.
      store.breakOwnshipTrack();
    }

    const getInputs = (): SimInputs => {
      const ownship = useChartPlotterStore.getState().ownship;
      const env = useEnvStore.getState();
      const rate = useSimStore.getState().rate;
      return {
        headingDeg: ownship.heading,
        stwKn: ownship.stw || 0,
        leewayDeg: leewayFromWind(ownship.heading, env.windDirDeg, env.windSpeedKn),
        tide: env.tide,
        rate,
      };
    };

    const source = new SimulatedGnssSource({
      startPosition,
      startHour: useEnvStore.getState().clockH,
      getInputs,
      intervalMs: 1000,
      onClock: (tHour) => useEnvStore.getState().setClockH(tHour),
    });

    useGnssStore.getState().setSource(source);
    source.start();

    return () => {
      source.stop();
      // Only clear if it's still the active source (guards against the
      // StrictMode double-mount and other races when the effect re-runs).
      if (useGnssStore.getState().source === source) {
        useGnssStore.getState().setSource(null);
      }
    };
  }, [running, sourceKind]);

  return null;
}
